import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Play, Heart, ArrowLeft, Lock, Star, Loader2 } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const InsiderVideo = () => {
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get("token");
  const [isValidating, setIsValidating] = useState(true);
  const [isValidAccess, setIsValidAccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const validateToken = async () => {
      if (!accessToken || accessToken.length < 10) {
        setIsValidating(false);
        setIsValidAccess(false);
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke("verify-access-token", {
          body: { token: accessToken },
        });

        if (error) {
          console.error("Token verification error:", error);
          setError("Unable to verify access. Please try again later.");
          setIsValidAccess(false);
        } else if (data?.valid) {
          setIsValidAccess(true);
        } else {
          setError(data?.error || "Invalid or expired access token");
          setIsValidAccess(false);
        }
      } catch (err) {
        console.error("Token validation failed:", err);
        setError("Unable to verify access. Please try again later.");
        setIsValidAccess(false);
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [accessToken]);

  if (isValidating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <Loader2 className="w-12 h-12 text-story-hope animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Verifying your access...</p>
        </motion.div>
      </div>
    );
  }

  if (!isValidAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-muted-foreground" />
          </div>
          <h1 className="text-2xl font-serif font-bold text-foreground mb-4">
            Exclusive Content
          </h1>
          <p className="text-muted-foreground mb-2">
            {error || "This page contains exclusive insider content for our generous backers."}
          </p>
          <p className="text-muted-foreground mb-6">
            Become a supporter to unlock access!
          </p>
          <Link to="/#donate">
            <Button className="bg-story-hope hover:bg-story-hope/90 text-black font-semibold">
              <Heart className="w-4 h-4 mr-2" />
              Become a Backer
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
      {/* Header */}
      <header className="p-6">
        <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>
      </header>

      <main className="container max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <Badge className="bg-story-peace/10 text-story-peace border-story-peace/20 mb-4">
            <Star className="w-4 h-4 mr-2" />
            EXCLUSIVE BACKER CONTENT
          </Badge>
          <h1 className="font-serif text-4xl md:text-5xl text-foreground mb-4">
            Thank You, Miracle Maker!
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Your generous support is making Tachen's reunion documentary possible. 
            Here's an exclusive behind-the-scenes look at our journey.
          </p>
        </motion.div>

        {/* Video Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Card className="border-2 border-story-hope/20 shadow-2xl overflow-hidden">
            <CardContent className="p-0">
              {/* Video Placeholder - Replace with actual video embed */}
              <div className="aspect-video bg-gradient-to-br from-story-peace/20 to-story-hope/20 flex items-center justify-center relative">
                <div className="absolute inset-0 bg-black/40" />
                <div className="relative z-10 text-center p-8">
                  <div className="w-20 h-20 bg-story-hope rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg hover:scale-110 transition-transform cursor-pointer">
                    <Play className="w-10 h-10 text-black ml-1" />
                  </div>
                  <h2 className="text-2xl font-serif text-white mb-2">
                    Insider Documentary Preview
                  </h2>
                  <p className="text-white/80">
                    Behind the scenes of Tachen's miracle reunion
                  </p>
                </div>
                {/* Replace this div with an actual video embed like:
                <iframe 
                  src="https://www.youtube.com/embed/YOUR_VIDEO_ID" 
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                /> 
                */}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Additional Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 grid md:grid-cols-2 gap-6"
        >
          <Card className="border border-muted">
            <CardContent className="p-6">
              <Heart className="w-8 h-8 text-story-hope mb-4" />
              <h3 className="text-xl font-semibold mb-2">Your Impact</h3>
              <p className="text-muted-foreground">
                As a shareholder in this project, you'll receive royalty participation 
                in all documentary proceeds. Your name will appear in the credits!
              </p>
            </CardContent>
          </Card>
          
          <Card className="border border-muted">
            <CardContent className="p-6">
              <Star className="w-8 h-8 text-story-peace mb-4" />
              <h3 className="text-xl font-semibold mb-2">Coming Soon</h3>
              <p className="text-muted-foreground">
                Watch for exclusive updates, more behind-the-scenes content, 
                and early access to the full documentary premiere.
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Share Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-12 text-center"
        >
          <p className="text-muted-foreground mb-4">
            Know someone who would love to support this story?
          </p>
          <Link to="/">
            <Button variant="outline" className="border-story-peace text-story-peace hover:bg-story-peace hover:text-background">
              Share the Campaign
            </Button>
          </Link>
        </motion.div>
      </main>
    </div>
  );
};

export default InsiderVideo;